import { defineStore } from 'pinia'
import { api, extractError } from '@/api/client'
import type { ProfileResponse, SubscriptionFilters, UserProfile } from '@/types'

interface State {
  profile: UserProfile | null
  loading: boolean
  error: string | null
}

export const useUserStore = defineStore('user', {
  state: (): State => ({
    profile: null,
    loading: false,
    error: null,
  }),

  getters: {
    serverSwitchEnabled(state): boolean {
      return state.profile?.server_switch_enabled === true
    },
    hasManualKeysForSwitch(): boolean {
      return (this.profile?.key_switches_cap ?? 0) > 0
    },
    subscriptionActive(state): boolean {
      return state.profile?.subscription_active === true
    },
    subscriptionFilters(state): SubscriptionFilters {
      return state.profile?.subscription_filters ?? {}
    },
  },

  actions: {
    async fetch(force = false): Promise<void> {
      if (this.profile && !force) return
      this.loading = true
      this.error = null
      try {
        const { data } = await api.get<ProfileResponse>('/api/user/profile')
        if (data.success && data.user) {
          this.profile = data.user
        }
      } catch (e) {
        this.error = extractError(e, 'Не удалось загрузить профиль')
      } finally {
        this.loading = false
      }
    },

    async enableConnectsFromRussia(): Promise<{
      success: boolean
      connects_from_russia?: boolean | null
      message?: string
    }> {
      const { data } = await api.post('/api/user/connects-from-russia/enable')
      if (data.success && this.profile) {
        this.profile.connects_from_russia = data.connects_from_russia
      }
      return data
    },

    async redeemConnectsFromRussia(code: string): Promise<{
      success: boolean
      connects_from_russia?: boolean | null
      message?: string
    }> {
      const { data } = await api.post('/api/user/connects-from-russia/redeem', { code })
      if (data.success && this.profile) {
        this.profile.connects_from_russia = data.connects_from_russia ?? false
      }
      return data
    },

    updateSwitchRemaining(remaining: number): void {
      if (this.profile) {
        this.profile.key_switches_remaining = remaining
      }
    },

    updateSubscriptionFilters(filters: SubscriptionFilters): void {
      if (this.profile) {
        this.profile.subscription_filters = { ...filters }
        return
      }
      this.profile = {
        subscription_filters: { ...filters },
      } as UserProfile
    },
  },
})
